import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { crudStatusEnum } from '../../../../constants';
import { useCreateRentalMutation, useUpdateRentalMutation } from '../../../../hooks/mutations/useRentalMutation';
import { useAuth } from '../../../../hooks/useAuth';
import { Rental } from '../../../../services/apiSLTP/materials/rentals';
import { capitalizeFirstLetter } from '../../../../utils/capitalize';
import CustomTextInput from '../../../ui/CustomTextInput';
import ToggleLine from '../../../ui/ToggleLine';
import ModalBody from '../../ModalBody';

interface ModalProps {
    rental: Rental | null;
    isEditMode?: boolean;
    showEditModal: boolean;
    setShowEditModal: React.Dispatch<React.SetStateAction<boolean>>;
    refetch: () => void;
}

const UpdateRentalModal: React.FC<ModalProps> = ({ rental, isEditMode = true, showEditModal, setShowEditModal, refetch }) => {
    const { t } = useTranslation();

    const [designation, setDesignation] = useState(rental?.designation || '');
    const [email, setEmail] = useState(rental?.email || '');
    const [codeProC, setCodeProC] = useState(rental?.codeProC || '');
    const [crudStatusEnumState, setCrudStatusState] = useState(rental?.crudStatus ?? crudStatusEnum.OK);
    const [displayOrder, setDisplayOrder] = useState(typeof rental?.displayOrder === 'number' ? rental?.displayOrder.toString() : '');

    const { user } = useAuth();

    const {
        mutate: mutateRental,
        isSuccess: isSuccessRental,
        isError: isErrorRental,
        isLoading: isLoadingRental,
        error: errorRental,
        data: dataRental,
    } = useUpdateRentalMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating rental:', error.message);
        },
    });

    const {
        mutate: addRental,
        isSuccess: isSuccessAdded,
        isError: isErrorAdded,
        isLoading: isLoadingAdded,
        error: errorAdded,
        data: dataAdded,
    } = useCreateRentalMutation({
        onSuccess: (data, variables) => {
            refetch();
        },
        onError: (error) => {
            console.error('Error updating rental:', error.message);
        },
    });

    return (
        <ModalBody
            title={t('rentalType')}
            subtitle={isEditMode ? t('update') : t('add')}
            open={showEditModal}
            handleClose={() => setShowEditModal(false)}
            disableSubmit={!(designation.length && codeProC.length)}
            onSubmit={() => {
                isEditMode
                    ? mutateRental({
                          rentalId: rental?._id || '',
                          data: {
                              designation,
                              crudStatus: crudStatusEnumState,
                              codeProC,
                              email,
                              displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                          },
                      })
                    : addRental({
                          designation,
                          codeProC,
                          crudStatus: crudStatusEnumState,
                          email,
                          displayOrder: !isNaN(parseFloat(displayOrder)) ? parseFloat(displayOrder) : null,
                      });
            }}
        >
            <CustomTextInput value={designation} label={t('name')} onChange={(text) => setDesignation(text)} errorText={t('error')} required />
            <CustomTextInput value={codeProC} label={t('codeProC')} onChange={(text) => setCodeProC(text)} errorText={t('error')} required />
            <ToggleLine
                checked={crudStatusEnumState === crudStatusEnum.OK}
                onChange={(checked) => setCrudStatusState(checked ? crudStatusEnum.OK : crudStatusEnum.INACTIVE)}
                title={capitalizeFirstLetter(t('active'))}
            />
            <CustomTextInput value={email} label={t('email')} onChange={(text) => setEmail(text)} errorText={t('error')} />
            <CustomTextInput value={displayOrder} onChange={setDisplayOrder} label={t('displayOrder')} type="number" />
        </ModalBody>
    );
};

export default UpdateRentalModal;
